'use client';

import { CaretRight, Check, Minus } from '@phosphor-icons/react/ssr';
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { checkboxBox } from '../lib/checkbox-styles';
import { menuStyles } from '../lib/menu-styles';

/**
 * shadcn DropdownMenu (Radix), restyled to the Figma Menu, Menu Item, Menu
 * Header and Menu Divider. Apps use `Menu` (components/lib).
 */
const DropdownMenu = (props: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) => (
  <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
);

const DropdownMenuTrigger = (
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>,
) => <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;

const DropdownMenuGroup = (
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Group>,
) => <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;

function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={menuStyles().content({
          className: cn(
            'max-h-(--radix-dropdown-menu-content-available-height) origin-(--radix-dropdown-menu-content-transform-origin)',
            className,
          ),
        })}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

function DropdownMenuItem({
  className,
  type = 'default',
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  type?: 'default' | 'danger';
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-type={type}
      className={menuStyles({ type }).item({ className })}
      {...props}
    />
  );
}

/** Figma Menu Item, Type=Checkbox: a Checkbox box before the label, brand/subtle when checked. */
function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  const boxState =
    checked === 'indeterminate' ? 'indeterminate' : checked ? 'checked' : 'unchecked';
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={menuStyles().item({ className })}
      checked={checked}
      {...props}
    >
      <span
        aria-hidden
        data-slot="checkbox"
        data-state={boxState}
        data-disabled={props.disabled ? '' : undefined}
        className={checkboxBox({ className: 'hover:inset-ring-border-strong' })}
      >
        <DropdownMenuPrimitive.ItemIndicator className="grid place-content-center">
          {checked === 'indeterminate' ? <Minus /> : <Check />}
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

const DropdownMenuRadioGroup = (
  props: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>,
) => (
  <DropdownMenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />
);

/** Figma Menu Item, Type=Default, Selected: brand/subtle fill + icon/brand check on the right. */
function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  const menu = menuStyles();
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={menu.item({ className })}
      {...props}
    >
      {children}
      <DropdownMenuPrimitive.ItemIndicator className={menu.check()}>
        <Check />
      </DropdownMenuPrimitive.ItemIndicator>
    </DropdownMenuPrimitive.RadioItem>
  );
}

function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={menuStyles().header({ className })}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={menuStyles().divider({ className })}
      {...props}
    />
  );
}

function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={menuStyles().shortcut({ className })}
      {...props}
    />
  );
}

const DropdownMenuSub = (
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>,
) => <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />;

function DropdownMenuSubTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger>) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      className={menuStyles().item({
        className: cn('data-[state=open]:bg-bg-muted', className),
      })}
      {...props}
    >
      {children}
      <CaretRight className="text-icon-tertiary ml-auto" />
    </DropdownMenuPrimitive.SubTrigger>
  );
}

function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        data-slot="dropdown-menu-sub-content"
        className={menuStyles().content({ className })}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
